import { PendingRow } from "./usePendingRows";

interface UseTableClipboardProps {
  visibleColumns: { name: string }[];
  pendingRows: PendingRow[];
  setPendingRows: (fn: (prev: PendingRow[]) => PendingRow[]) => void;
  addNewRow: () => void;
}

export function useTableClipboard(props: UseTableClipboardProps) {
  const copy = async (rowId?: string) => {
    let row = props.pendingRows[0];

    if (rowId) {
      row = props.pendingRows.find(r => r.id === rowId) ?? props.pendingRows[0];
    }

    const values = props.visibleColumns.map(col =>
      (row.data[col.name] ?? "").toString()
    );

    await navigator.clipboard.writeText(values.join("\t"));
  };


  const paste = async (
    rowId: string,
    fieldName: string
  ) => {
    const text = await navigator.clipboard.readText();
    pasteCells(text, rowId, fieldName);
  };


  const pasteValues = async (rowId: string, fieldName: string) => {
    const text = await navigator.clipboard.readText();
    props.setPendingRows(prev =>
      prev.map(r =>
        r.id === rowId ? { ...r, data: { ...r.data, [fieldName]: text } } : r
      )
    );
  };


  const pasteCells = (
    text: string,
    rowId: string,
    fieldName: string
  ) => {
    const rows = text
      .trim()
      .split("\n")
      .map(r => r.split("\t"));

    if (!rows.length) return;

    const startCol = props.visibleColumns.findIndex(c => c.name === fieldName);
    const startRow = props.pendingRows.findIndex(r => r.id === rowId);

    if (startCol === -1 || startRow === -1) return;

    const need = Math.max(
      0,
      rows.length - (props.pendingRows.length - startRow)
    );

    for (let i = 0; i < need; i++) {
      props.addNewRow();
    }

    props.setPendingRows(prev => {
      const updated = [...prev];

      rows.forEach((r, rIdx) => {
        const targetRow = startRow + rIdx;
        if (targetRow >= updated.length) return;

        r.forEach((cell, cIdx) => {
          const targetCol = startCol + cIdx;
          if (targetCol >= props.visibleColumns.length) return;

          const col = props.visibleColumns[targetCol];
          updated[targetRow].data[col.name] = cell.trim();
        });
      });

      return updated;
    });
  };

  const handlePaste = (
    e: React.ClipboardEvent,
    rowId: string,
    fieldName: string
  ) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text");
    pasteCells(text, rowId, fieldName);
  };


  return {
    copy,
    paste,
    pasteValues,
    handlePaste,
  };
}
